// src/pages/AuthLanding.jsx
import React, { useMemo, useState } from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Divider,
  Alert,
  Link as MUILink,
} from "@mui/material";
import { useNavigate, useLocation, Link } from "react-router-dom";
import EmailIcon from "@mui/icons-material/Email";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import GoogleIcon from "@mui/icons-material/Google";
import AppleIcon from "@mui/icons-material/Apple";
import { authService } from "../services/resources.service";
import { useMessage } from "../i18n/locale";
import { storeSessionToken } from "../services/authSession";
import { getSafeAuthRedirect } from "../services/authRedirect";
import AppVersion from "../components/AppVersion";

async function loginWithEmail({ email, password }) {
  return authService.signIn({ email, password });
}
async function loginWithProvider(provider) {
  return authService.signIn({ provider });
}

export default function AuthLanding({ onAuth }) {
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = getSafeAuthRedirect(
    new URLSearchParams(location.search).get("redirect")
  );
  const browseWithoutSigningIn = useMessage("authBrowseWithoutSigningIn");
  const activationPending = useMessage("activationPending");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const canSubmit = useMemo(
    () => email.trim().length > 5 && password.trim().length >= 4,
    [email, password]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || loading) return;
    setLoading(true);
    setError("");
    try {
      const res = await loginWithEmail({ email, password });
      storeSessionToken(res.token);
      onAuth?.(res.user);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(err.message || "Failed to sign in");
    } finally {
      setLoading(false);
    }
  };

  const oauth = async (provider) => {
    if (loading) return;
    setLoading(true);
    setError("");
    try {
      const res = await loginWithProvider(provider);
      storeSessionToken(res.token);
      onAuth?.(res.user);
      navigate(redirectTo, { replace: true });
    } catch {
      setError("OAuth sign-in failed. Try email instead.");
    } finally {
      setLoading(false);
    }
  };

  const browseWithoutAccount = () => {
    navigate(redirectTo, { replace: true });
  };

  return (
    <Box sx={{ minHeight: "100dvh", display: "grid", placeItems: "center", p: 2 }}>
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 440,
          p: 2,
          borderRadius: 3,
          border: 1,
          borderColor: "divider",
        }}
      >
        <Stack spacing={2}>
          {location.state?.activationPending && (
            <Alert severity="success">{activationPending}</Alert>
          )}
          {/* Brand / Title */}
          <Stack spacing={0.5} alignItems="center" sx={{ textAlign: "center" }}>
            <Box
              component="img"
              src="/logo.png"
              alt="Band-Joe logo"
              sx={{ width: 88, height: 88, objectFit: "contain", mb: 0.5 }}
            />
            <Typography variant="h5" fontWeight={900}>
              Band-Joe
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Sign in to book gigs, message, and manage your page.
            </Typography>
          </Stack>

          {/* Email form */}
          <Box component="form" onSubmit={handleSubmit}>
            <Stack spacing={1.25}>
              <TextField
                label="Email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                autoFocus
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="Password"
                type={showPw ? "text" : "password"}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPw((s) => !s)} edge="end" aria-label="toggle password visibility">
                        {showPw ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Stack direction="row" alignItems="center" justifyContent="flex-end">
                <MUILink component={Link} to="/auth/reset" underline="hover">
                  Forgot password?
                </MUILink>
              </Stack>
              {!!error && <Alert severity="error">{error}</Alert>}
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={!canSubmit || loading}
                fullWidth
              >
                {loading ? "Signing in…" : "Sign in"}
              </Button>
            </Stack>
          </Box>

          {/* Sign up / unauthenticated browsing */}
          <Stack spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              New here?{" "}
              <MUILink
                component={Link}
                to={`/auth/signup?redirect=${encodeURIComponent(redirectTo)}`}
                underline="hover"
              >
                Create an account
              </MUILink>
            </Typography>
            <Button
              size="small"
              onClick={browseWithoutAccount}
              disabled={loading}
            >
              {browseWithoutSigningIn}
            </Button>
          </Stack>

          <Typography variant="caption" color="text.secondary" textAlign="center">
            Before using Band-Joe, please read our{" "}
            <MUILink component={Link} to="/terms" underline="hover">
              Terms
            </MUILink>{" "}
            and{" "}
            <MUILink component={Link} to="/privacy" underline="hover">
              Privacy Policy
            </MUILink>
            .
          </Typography>
          <AppVersion sx={{ alignSelf: "center" }} />
        </Stack>
      </Paper>

    </Box>
  );
}
